import DatePickerInput from '../../../form/DatePickerInput';
import Select from '../../../form/Select';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useCallback, useEffect } from 'react';
import queryString from 'query-string';
import { useLoginStore } from '../../../../store/useLogin';
import { useFranchiseStore } from '../../../../store/useFranchise';
import { formatDate } from '../../../../util/util';

interface IBokbiHistoryFilter {
  getFilteredData: (params: any) => void;
}

const BokbiHistoryFilter = ({ getFilteredData }: IBokbiHistoryFilter) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useLoginStore();
  const { allFranchises, getAllFranchises } = useFranchiseStore();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [pageSize, setPageSize] = useState<string>('20');
  const [chargeType, setChargeType] = useState<string>('');
  const [franchise, setFranchise] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.StartDt) {
      return new Date(currentQuery.StartDt as string);
    }
    const today = new Date();
    return new Date(today.setDate(today.getDate() - 7));
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.EndDt) {
      return new Date(currentQuery.EndDt as string);
    }
    return new Date();
  });

  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState<boolean>(false);
  const [franchiseSearchText, setFranchiseSearchText] = useState<string>('');

  useEffect(() => {
    if (role === 'ROLE_ADMIN') {
      getAllFranchises();
    }
  }, [getAllFranchises, role]);

  useEffect(() => {
    const currentQuery = queryString.parse(location.search);

    if (currentQuery.size) setPageSize(currentQuery.size as string);
    else setPageSize('20');

    if (currentQuery.chargeType) setChargeType(currentQuery.chargeType as string);
    else setChargeType('');

    if (currentQuery.franchiseId) setFranchise(currentQuery.franchiseId as string);
    else setFranchise('');

    if (currentQuery.StartDt) {
      setStartDate(new Date(currentQuery.StartDt as string));
    } else {
      const today = new Date();
      setStartDate(new Date(today.setDate(today.getDate() - 7)));
    }
    if (currentQuery.EndDt) {
      setEndDate(new Date(currentQuery.EndDt as string));
    } else {
      setEndDate(new Date());
    }
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.franchise-dropdown-container')) {
        setShowFranchiseDropdown(false);
        setFranchiseSearchText('');
      }
    };
    if (showFranchiseDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFranchiseDropdown]);

  const updateQueryString = useCallback(
    (updatedFields: Record<string, string | undefined>) => {
      const currentQuery = queryString.parse(location.search);
      const updatedQuery = { ...currentQuery, ...updatedFields };
      const newQueryString = queryString.stringify(updatedQuery, { skipEmptyString: true, skipNull: true });
      navigate(`?${newQueryString}`);
    },
    [location.search, navigate]
  );

  const onChangePageSize = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setPageSize(value);
    updateQueryString({ size: value, page: '0' });
  }, [updateQueryString]);

  const onChangeChargeType = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'CHARGE' | 'ORDER_PAY' | 'CANCEL';
    setChargeType(value);
    updateQueryString({ chargeType: value, page: '0' });
  }, [updateQueryString]);

  const handleFranchiseSelect = useCallback((franchiseId: string) => {
    setFranchise(franchiseId);
    updateQueryString({ franchiseId: franchiseId || undefined, page: '0' });
  }, [updateQueryString]);

  const handleStartDateChange = useCallback((date: Date | null) => {
    setStartDate(date);
    const StartDt = date ? formatDate(date)['yyyy-mm-ddTHH:mm:ss'] : undefined;
    updateQueryString({ StartDt });
  }, [updateQueryString]);

  const handleEndDateChange = useCallback((date: Date | null) => {
    setEndDate(date);
    const EndDt = date ? formatDate(date)['yyyy-mm-ddTHH:mm:ss'] : undefined;
    updateQueryString({ EndDt });
  }, [updateQueryString]);

  const chargeTypeOptions = [
    { option: '유형', value: '' },
    { option: '충전', value: 'CHARGE' },
    { option: '사용', value: 'ORDER_PAY' },
    { option: '취소', value: 'CANCEL' },
  ];

  const sizeOptions = [
    { option: '20개씩', value: '20' },
    { option: '50개씩', value: '50' },
    { option: '100개씩', value: '100' },
    { option: '300개씩', value: '300' },
    { option: '500개씩', value: '500' },
  ];

  const selectedFranchiseName = allFranchises.find(f => f.id === franchise)?.companyName || '가맹점 선택';

  return (
    <div className="!w-full">
      <div className="!flex !justify-between !items-center">
        <button
          onClick={() => setIsFilterOpen(prev => !prev)}
          className="md:!hidden !px-3 !py-1.5 !border !rounded !text-sm"
        >
          {isFilterOpen ? '닫기' : '검색필터 열기'}
        </button>
      </div>
      <form 
        onSubmit={(e) => e.preventDefault()} 
        className={`
          ${isFilterOpen ? '!flex' : '!hidden'}
          md:!flex !mt-4 !flex-col !gap-4 md:!flex-row md:!items-center md:!justify-between !w-full
        `}
      >
        <div className="!flex !items-center !gap-3 !flex-wrap">
          <div className="!flex !items-center !gap-1">
            <DatePickerInput
              value={startDate}
              onChange={handleStartDateChange}
              placeholder="시작일"
              width={130}
            />
            <span className="!text-gray-400">~</span>
            <DatePickerInput
              value={endDate}
              onChange={handleEndDateChange}
              placeholder="종료일"
              width={130}
            />
          </div>
          <Select
            options={chargeTypeOptions}
            onChange={onChangeChargeType}
            value={chargeType}
            style={{ width: '120px' }}
          />
          {role === 'ROLE_ADMIN' && (
            <div className="!relative franchise-dropdown-container" style={{ width: '200px' }}>
              <button
                type="button"
                onClick={() => setShowFranchiseDropdown(!showFranchiseDropdown)}
                className="!w-full !flex !items-center !justify-between !px-3 !py-2 !bg-white !border !border-gray-300 !rounded-md !text-sm !font-medium !text-gray-700 hover:!bg-gray-50 !transition-colors"
              >
                <span className="!truncate">{selectedFranchiseName}</span>
                <svg className="!w-4 !h-4 !ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showFranchiseDropdown && (
                <div className="!absolute !mt-1 !w-full !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-10">
                  <div className="!p-2 !border-b !border-gray-200">
                    <div className="!relative">
                      <input
                        type="text"
                        value={franchiseSearchText}
                        onChange={(e) => setFranchiseSearchText(e.target.value)}
                        placeholder="가맹점 검색..."
                        className="!w-full !pl-8 !pr-3 !py-1.5 !text-sm !border !border-gray-300 !rounded-md focus:!outline-none focus:!ring-2 focus:!ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <svg className="!absolute !left-2.5 !top-1/2 !transform !-translate-y-1/2 !w-4 !h-4 !text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  <div className="!max-h-60 !overflow-y-auto">
                    {allFranchises
                      .filter(f => f.companyName && f.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase()))
                      .map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => {
                            handleFranchiseSelect(f.id);
                            setShowFranchiseDropdown(false);
                            setFranchiseSearchText('');
                          }}
                          className="!w-full !px-3 !py-2 !text-left !text-sm !text-gray-700 hover:!bg-gray-100 !transition-colors !truncate"
                        >
                          {f.companyName}
                        </button>
                      ))}
                  </div>

                  {franchise && (
                    <div className="!border-t !border-gray-200 !px-3 !py-2">
                      <button
                        type="button"
                        onClick={() => {
                          handleFranchiseSelect('');
                          setShowFranchiseDropdown(false);
                          setFranchiseSearchText('');
                        }}
                        className="!text-sm !text-red-600 hover:!text-red-700"
                      >
                        선택 취소
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="!flex !items-center self-start md:self-center">
          <Select
            options={sizeOptions}
            onChange={onChangePageSize}
            value={pageSize}
            style={{ width: '120px' }}
          />
        </div>
      </form>
    </div>
  );
};

export default BokbiHistoryFilter;